import React, { useReducer, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { QUESTION_TYPES } from 'shared/constants/survey';
import { GRADE, NPS, FIND } from 'shared/constants/questionCategories';
import { hasCategory } from 'shared/utils/categories';
import ComparisonTable from '../Charts/ComparisonTable';
import ScatterChart from '../Charts/ScatterChart';
import messages from './messages';

const chartSettingsReducer = (state, action = {}) => ({ ...state, ...action });

const DEFAULT_CHART_SETTINGS = {
  type: 'scatter',
  displayMode: 'percentage',
  showLabels: true,
};

const precision = 2;

const getCompareToChartSettings = (question) => {
  if (question.settings.type === QUESTION_TYPES.FIND || hasCategory(question.categories || question.scoreCategories, [FIND])) { return { yAxisMarker: 50 }; }
  if (question.settings.type === QUESTION_TYPES.GRADE || hasCategory(question.categories || question.scoreCategories, [GRADE])) { return { yAxisMarker: 3.5 }; }
  if (question.settings.type === QUESTION_TYPES.NPS || hasCategory(question.categories || question.scoreCategories, [NPS])) { return { yAxisMarker: 0 }; }
  return {};
};

const getChartSettings = (props) => {
  const chartSettings = {
    ...DEFAULT_CHART_SETTINGS,
    ...getCompareToChartSettings(props.comparedTo),
  };

  if (props.chartSettings) {
    return {
      ...chartSettings,
      ...props.chartSettings,
    };
  }

  return chartSettings;
};

const ComparisonResult = (props) => {
  const { formatMessage } = useIntl();
  const { question, comparedTo, segments, isPrint, addToReport, onChartSettingsChanged, onSelectChartSegment } = props;
  const [chartSettings, dispatch] = useReducer(chartSettingsReducer, getChartSettings(props));

  useEffect(() => {
    if (onChartSettingsChanged) {
      onChartSettingsChanged(chartSettings);
    }
  }, [chartSettings]);

  useEffect(() => {
    dispatch(getChartSettings(props));
  }, [props.chartSettings, comparedTo]);

  const title = useMemo(() => formatMessage(messages.comparisonReportChartTitle, { question: question.title, comparedTo: comparedTo.title }), [question, comparedTo]);

  const toolbox = useMemo(() => ({
    switchChartType: (type) => dispatch({ type }),
    switchDisplayMode: (displayMode) => dispatch({ displayMode }),
    showPercentage: true,
    chartTypes: ['scatter'],
    addToReport: addToReport && ((data) => {
      addToReport({
        ...data,
        title,
        chartSettings: {
          ...chartSettings,
          ...data?.chartSettings,
        },
      });
    }),
  }), [chartSettings, addToReport]);

  if (chartSettings.type === 'table') {
    return (
      <ComparisonTable
        question={question}
        comparedTo={comparedTo}
        segments={segments}
        chartSettings={chartSettings}
        toolbox={toolbox}
        precision={precision}
      />
    );
  }

  return (
    <ScatterChart
      title={title}
      question={question}
      comparedTo={comparedTo}
      segments={segments}
      chartSettings={chartSettings}
      isPrint={isPrint}
      toolbox={toolbox}
      onSelectChartSegment={onSelectChartSegment}
      precision={precision}
    />
  );
};

export default ComparisonResult;
